/*
 * <bc-countdown data-ends-at="<epoch seconds>"> — a live "ends in" ticker.
 *
 * Zero dependencies, no imports, no module graph: it is loaded with a plain
 * `defer` <script> from snippets/bc-campaign-announcement.liquid, and only while
 * a campaign is actually counting down.
 *
 * PROGRESSIVE ENHANCEMENT IS THE POINT. The element ships from Liquid already
 * containing readable static text ("Ends Aug 28") inside
 * [data-bc-countdown-text]. With JS off, blocked, or this file 404ing, the
 * shopper still sees the end date. When this runs it turns that caption into
 * "Ends in" and appends segmented digit groups (days · hours · minutes ·
 * seconds) that it updates in place every second — nodes are built ONCE, only
 * textContent changes on tick, so there is no per-second layout churn.
 *
 * data-ends-at is epoch SECONDS (see the snippet's comment: an offset-less ISO
 * string would be read as browser-local time and a "+0000" offset is not
 * guaranteed to parse).
 *
 * Ticking stops on its own at zero, and the element hides itself — a campaign
 * whose clock has run out should not advertise "00:00" until the next price
 * reconcile pulls the mirror.
 */
(function () {
  'use strict';

  const TEXT_SELECTOR = '[data-bc-countdown-text]';
  const LIVE_CAPTION = 'Ends in';
  const UNITS = [
    { key: 'd', label: 'd', mod: null, div: 86400 },
    { key: 'h', label: 'h', mod: 86400, div: 3600 },
    { key: 'm', label: 'm', mod: 3600, div: 60 },
    { key: 's', label: 's', mod: 60, div: 1 },
  ];

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function parts(msRemaining) {
    const total = Math.floor(msRemaining / 1000);
    return {
      d: Math.floor(total / 86400),
      h: Math.floor((total % 86400) / 3600),
      m: Math.floor((total % 3600) / 60),
      s: total % 60,
    };
  }

  function el(tag, className, text) {
    const node = document.createElement(tag);
    node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  class BcCountdown extends HTMLElement {
    connectedCallback() {
      const seconds = Number(this.dataset.endsAt);
      // Not a usable deadline: leave the server-rendered static text alone.
      if (!Number.isFinite(seconds) || seconds <= 0) return;

      this.endsAtMs = seconds * 1000;
      const caption = this.querySelector(TEXT_SELECTOR);
      if (!caption) return;

      caption.classList.add('bc-cd__caption');
      caption.textContent = LIVE_CAPTION;
      this.setAttribute('role', 'timer');
      this.setAttribute('aria-live', 'off');

      const units = el('span', 'bc-cd__units');
      this.nums = {};
      this.unitEls = {};
      for (const u of UNITS) {
        const unit = el('span', 'bc-cd__unit bc-cd__unit--' + u.key);
        const num = el('span', 'bc-cd__num', '00');
        unit.appendChild(num);
        unit.appendChild(el('span', 'bc-cd__lbl', u.label));
        units.appendChild(unit);
        this.nums[u.key] = num;
        this.unitEls[u.key] = unit;
      }
      this.appendChild(units);

      this.tick();
      this.timer = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = undefined;
    }

    tick() {
      const remaining = this.endsAtMs - Date.now();

      if (remaining <= 0) {
        this.disconnectedCallback();
        this.hidden = true;
        return;
      }

      const p = parts(remaining);
      // Days only when there are any; under an hour, drop hours too so the
      // final stretch reads "12m 30s" rather than "00h 12m 30s".
      this.unitEls.d.hidden = p.d === 0;
      this.unitEls.h.hidden = p.d === 0 && p.h === 0;

      const d = String(p.d);
      if (this.nums.d.textContent !== d) this.nums.d.textContent = d;
      const h = pad(p.h);
      if (this.nums.h.textContent !== h) this.nums.h.textContent = h;
      const m = pad(p.m);
      if (this.nums.m.textContent !== m) this.nums.m.textContent = m;
      this.nums.s.textContent = pad(p.s);

      this.setAttribute(
        'aria-label',
        LIVE_CAPTION + ' ' + (p.d ? p.d + ' days ' : '') + p.h + ' hours ' + p.m + ' minutes',
      );
    }
  }

  if (!customElements.get('bc-countdown')) {
    customElements.define('bc-countdown', BcCountdown);
  }
})();
